import { useEffect, type ComponentType } from 'react';
import {
  createBrowserRouter,
  Outlet,
  ScrollRestoration,
  useLocation,
  type RouteObject,
} from 'react-router';
import { telemetry } from '@/telemetry/telemetry';
import { PublicOnly, RequireAdmin, RequireSession } from './guards';
import { AppShell } from './layout/AppShell';
import { PublicLayout } from './layout/PublicLayout';
import { ErrorPage, NotFoundPage } from './pages/StatusPages';
import { HomePage } from '@/features/home/HomePage';

/**
 * A page loaded when first visited (tasks.md T204): only the shell and Home are
 * in the first download (a signed-in visit, the common case), so a first visit stays within the
 * bundle budget; the rest (and the libraries only they use) load on demand.
 */
function page<M>(load: () => Promise<M>, pick: (module: M) => ComponentType) {
  return { lazy: async () => ({ Component: pick(await load()) }) };
}

/** Tracks the screen for telemetry and restores scroll on back/forward (spec US2 scenario 5). */
function RootLayout() {
  const { pathname } = useLocation();
  useEffect(() => telemetry.trackRoute(pathname), [pathname]);
  return (
    <>
      <Outlet />
      <ScrollRestoration />
    </>
  );
}

/**
 * Every route of contracts/web-routes.md. 🔓 onboarding pages sit under
 * PublicOnly, 🔒 pages under RequireSession + the app shell, 🛡 pages under
 * RequireAdmin as well.
 */
const appRoutes: RouteObject[] = [
  // 🔓 Onboarding & auth
  {
    element: <PublicLayout />,
    children: [
      {
        element: <PublicOnly />,
        children: [
          {
            path: '/welcome',
            ...page(
              () => import('@/features/onboarding/WelcomePage'),
              (m) => m.WelcomePage,
            ),
          },
          {
            path: '/signup',
            ...page(
              () => import('@/features/onboarding/SignUpPage'),
              (m) => m.SignUpPage,
            ),
          },
          {
            path: '/signup/pledge',
            ...page(
              () => import('@/features/onboarding/PledgePage'),
              (m) => m.PledgePage,
            ),
          },
          {
            path: '/login',
            ...page(
              () => import('@/features/onboarding/LoginPage'),
              (m) => m.LoginPage,
            ),
          },
          {
            path: '/forgot-password',
            ...page(
              () => import('@/features/onboarding/ForgotPasswordPage'),
              (m) => m.ForgotPasswordPage,
            ),
          },
        ],
      },
      {
        // FR-004: the page the reset email links to; open to signed-in members too.
        path: '/reset-password',
        ...page(
          () => import('@/features/onboarding/ResetPasswordPage'),
          (m) => m.ResetPasswordPage,
        ),
      },
      {
        path: '/info/:infoKey',
        ...page(
          () => import('@/features/onboarding/InfoPage'),
          (m) => m.InfoPage,
        ),
      },
    ],
  },

  // 🔒 Signed in
  {
    element: <RequireSession />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          {
            path: '/verify-phone',
            ...page(
              () => import('@/features/onboarding/PhoneVerificationPage'),
              (m) => m.PhoneVerificationPage,
            ),
          },
        ],
      },
      {
        element: <AppShell />,
        children: [
          { path: '/', element: <HomePage /> },
          {
            path: '/browse',
            ...page(
              () => import('@/features/help-exchange/BrowsePage'),
              (m) => m.BrowsePage,
            ),
          },
          {
            path: '/profile',
            ...page(
              () => import('@/features/profile/ProfilePage'),
              (m) => m.ProfilePage,
            ),
          },

          // Help exchange
          {
            path: '/posts/new',
            ...page(
              () => import('@/features/help-exchange/CreatePostPage'),
              (m) => m.CreatePostPage,
            ),
          },
          {
            path: '/posts/:postId',
            ...page(
              () => import('@/features/help-exchange/PostDetailPage'),
              (m) => m.PostDetailPage,
            ),
          },
          {
            path: '/posts/:postId/edit',
            ...page(
              () => import('@/features/help-exchange/CreatePostPage'),
              (m) => m.CreatePostPage,
            ),
          },
          {
            path: '/posts/:postId/matches',
            ...page(
              () => import('@/features/help-exchange/MatchingPage'),
              (m) => m.MatchingPage,
            ),
          },
          {
            path: '/commitments/:commitmentId',
            ...page(
              () => import('@/features/help-exchange/CommitmentPage'),
              (m) => m.CommitmentPage,
            ),
          },
          {
            path: '/commitments/:commitmentId/kudos',
            ...page(
              () => import('@/features/help-exchange/KudosPage'),
              (m) => m.KudosPage,
            ),
          },
          {
            path: '/report',
            ...page(
              () => import('@/features/help-exchange/ReportPage'),
              (m) => m.ReportPage,
            ),
          },

          // Messaging & calls: the list and the open page share one layout (two panes when wide).
          {
            path: '/messages',
            ...page(
              () => import('@/features/messaging/MessagesLayout'),
              (m) => m.MessagesLayout,
            ),
            children: [
              { index: true, element: null },
              {
                path: 'group',
                ...page(
                  () => import('@/features/messaging/GroupChatPage'),
                  (m) => m.GroupChatPage,
                ),
                handle: { immersive: true },
              },
              {
                path: 'group/info',
                ...page(
                  () => import('@/features/messaging/GroupInfoPage'),
                  (m) => m.GroupInfoPage,
                ),
              },
              {
                path: 'new',
                ...page(
                  () => import('@/features/messaging/NewChatPage'),
                  (m) => m.NewChatPage,
                ),
              },
              {
                path: 'new-group',
                ...page(
                  () => import('@/features/messaging/NewGroupPage'),
                  (m) => m.NewGroupPage,
                ),
              },
              {
                path: 'invitations',
                ...page(
                  () => import('@/features/messaging/InvitationsPage'),
                  (m) => m.InvitationsPage,
                ),
              },
              {
                path: 'search',
                ...page(
                  () => import('@/features/messaging/SearchPage'),
                  (m) => m.SearchPage,
                ),
              },
              {
                path: 'archived',
                ...page(
                  () => import('@/features/messaging/ArchivedPage'),
                  (m) => m.ArchivedPage,
                ),
              },
              {
                path: 'broadcast',
                ...page(
                  () => import('@/features/messaging/BroadcastPage'),
                  (m) => m.BroadcastPage,
                ),
              },
              {
                path: 'broadcasts',
                ...page(
                  () => import('@/features/messaging/BroadcastsPage'),
                  (m) => m.BroadcastsPage,
                ),
              },
              {
                path: 'broadcasts/:broadcastId',
                ...page(
                  () => import('@/features/messaging/BroadcastDetailPage'),
                  (m) => m.BroadcastDetailPage,
                ),
              },
              {
                path: ':conversationId',
                ...page(
                  () => import('@/features/messaging/ChatPage'),
                  (m) => m.ChatPage,
                ),
                handle: { immersive: true },
              },
              {
                path: ':conversationId/contact',
                ...page(
                  () => import('@/features/messaging/ContactPage'),
                  (m) => m.ContactPage,
                ),
              },
              {
                path: ':conversationId/media',
                ...page(
                  () => import('@/features/messaging/MediaPage'),
                  (m) => m.MediaPage,
                ),
              },
              {
                path: ':conversationId/wallpaper',
                ...page(
                  () => import('@/features/messaging/WallpaperPage'),
                  (m) => m.WallpaperPage,
                ),
              },
            ],
          },
          {
            path: '/stories',
            ...page(
              () => import('@/features/home/StoriesPage'),
              (m) => m.StoriesPage,
            ),
          },
          {
            path: '/stories/view/:authorId',
            ...page(
              () => import('@/features/home/StoryViewerPage'),
              (m) => m.StoryViewerPage,
            ),
          },
          {
            path: '/connect',
            ...page(
              () => import('@/features/messaging/ConnectPage'),
              (m) => m.ConnectPage,
            ),
          },
          {
            path: '/connect/:code',
            ...page(
              () => import('@/features/messaging/ConnectPage'),
              (m) => m.ConnectPage,
            ),
          },
          {
            path: '/connections',
            ...page(
              () => import('@/features/messaging/ConnectionsPage'),
              (m) => m.ConnectionsPage,
            ),
          },
          {
            path: '/calls',
            ...page(
              () => import('@/features/calls/CallLogPage'),
              (m) => m.CallLogPage,
            ),
          },

          // Home, promotions & events
          {
            path: '/impact',
            ...page(
              () => import('@/features/home/ImpactPage'),
              (m) => m.ImpactPage,
            ),
          },
          {
            path: '/members/:memberId',
            ...page(
              () => import('@/features/home/PosterProfilePage'),
              (m) => m.PosterProfilePage,
            ),
          },
          {
            path: '/promotions',
            ...page(
              () => import('@/features/promotions-events/PromotionsPage'),
              (m) => m.PromotionsPage,
            ),
          },
          {
            path: '/promotions/mine',
            ...page(
              () => import('@/features/promotions-events/MyPromotionsPage'),
              (m) => m.MyPromotionsPage,
            ),
          },
          {
            path: '/promotions/new',
            ...page(
              () => import('@/features/promotions-events/CreatePromotionPage'),
              (m) => m.CreatePromotionPage,
            ),
          },
          {
            path: '/promotions/:promotionId/edit',
            ...page(
              () => import('@/features/promotions-events/CreatePromotionPage'),
              (m) => m.CreatePromotionPage,
            ),
          },
          {
            path: '/promotions/:promotionId/code',
            ...page(
              () => import('@/features/promotions-events/PromotionCodePage'),
              (m) => m.PromotionCodePage,
            ),
          },
          {
            path: '/promotions/redeem',
            ...page(
              () => import('@/features/promotions-events/RedeemPage'),
              (m) => m.RedeemPage,
            ),
          },
          {
            path: '/events',
            ...page(
              () => import('@/features/promotions-events/EventsPage'),
              (m) => m.EventsPage,
            ),
          },
          {
            path: '/events/new',
            ...page(
              () => import('@/features/promotions-events/CreateEventPage'),
              (m) => m.CreateEventPage,
            ),
          },
          {
            path: '/events/:eventId/edit',
            ...page(
              () => import('@/features/promotions-events/CreateEventPage'),
              (m) => m.CreateEventPage,
            ),
          },

          // Profile
          {
            path: '/profile/edit',
            ...page(
              () => import('@/features/profile/EditProfilePage'),
              (m) => m.EditProfilePage,
            ),
          },
          {
            path: '/profile/trust',
            ...page(
              () => import('@/features/profile/TrustHistoryPage'),
              (m) => m.TrustHistoryPage,
            ),
          },
          {
            path: '/profile/posts',
            ...page(
              () => import('@/features/profile/MyPostsPage'),
              (m) => m.MyPostsPage,
            ),
          },
          {
            // FR-047: the announcements from the Kindra team, which a notification or banner leads to.
            path: '/announcements',
            ...page(
              () => import('@/features/profile/AnnouncementsPage'),
              (m) => m.AnnouncementsPage,
            ),
          },
          {
            path: '/profile/warnings',
            ...page(
              () => import('@/features/profile/WarningsPage'),
              (m) => m.WarningsPage,
            ),
          },
          {
            // specs/003 #12
            path: '/profile/devices',
            ...page(
              () => import('@/features/profile/DevicesPage'),
              (m) => m.DevicesPage,
            ),
          },
          {
            path: '/profile/notifications',
            ...page(
              () => import('@/features/profile/NotificationSettingsPage'),
              (m) => m.NotificationSettingsPage,
            ),
          },

          // 🛡 Admin
          {
            element: <RequireAdmin />,
            children: [
              {
                path: '/admin',
                ...page(
                  () => import('@/features/admin/AdminLayout'),
                  (m) => m.AdminLayout,
                ),
                children: [
                  {
                    index: true,
                    ...page(
                      () => import('@/features/admin/AdminDashboardPage'),
                      (m) => m.AdminDashboardPage,
                    ),
                  },
                  {
                    path: 'members',
                    ...page(
                      () => import('@/features/admin/AdminMembersPage'),
                      (m) => m.AdminMembersPage,
                    ),
                  },
                  {
                    path: 'members/:memberId',
                    ...page(
                      () => import('@/features/admin/AdminMemberDetailPage'),
                      (m) => m.AdminMemberDetailPage,
                    ),
                  },
                  {
                    path: 'reports',
                    ...page(
                      () => import('@/features/admin/AdminReportsPage'),
                      (m) => m.AdminReportsPage,
                    ),
                  },
                  {
                    path: 'reports/:reportId/mediation',
                    ...page(
                      () => import('@/features/admin/MediationPage'),
                      (m) => m.MediationPage,
                    ),
                  },
                  {
                    path: 'businesses',
                    ...page(
                      () => import('@/features/admin/AdminBusinessesPage'),
                      (m) => m.AdminBusinessesPage,
                    ),
                  },
                  {
                    path: 'promotions',
                    ...page(
                      () => import('@/features/admin/AdminPromotionsPage'),
                      (m) => m.AdminPromotionsPage,
                    ),
                  },
                  {
                    path: 'promotions/:promotionId',
                    ...page(
                      () => import('@/features/admin/AdminPromotionDetailPage'),
                      (m) => m.AdminPromotionDetailPage,
                    ),
                  },
                  {
                    path: 'organizations',
                    ...page(
                      () => import('@/features/admin/AdminOrganizationsPage'),
                      (m) => m.AdminOrganizationsPage,
                    ),
                  },
                  {
                    path: 'verifications',
                    ...page(
                      () => import('@/features/admin/AdminVerificationsPage'),
                      (m) => m.AdminVerificationsPage,
                    ),
                  },
                  {
                    path: 'events',
                    ...page(
                      () => import('@/features/admin/AdminEventsPage'),
                      (m) => m.AdminEventsPage,
                    ),
                  },
                  {
                    path: 'announcements',
                    ...page(
                      () => import('@/features/admin/AdminAnnouncementsPage'),
                      (m) => m.AdminAnnouncementsPage,
                    ),
                  },
                  {
                    path: 'posts',
                    ...page(
                      () => import('@/features/admin/AdminPostsPage'),
                      (m) => m.AdminPostsPage,
                    ),
                  },
                  {
                    path: 'stats/:category?',
                    ...page(
                      () => import('@/features/admin/AdminStatsPage'),
                      (m) => m.AdminStatsPage,
                    ),
                  },
                ],
              },
            ],
          },

          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

/** The UI gallery for visual baselines (tasks.md T061/T062): never part of a production build. */
const galleryRoutes: RouteObject[] =
  import.meta.env.DEV || import.meta.env.MODE === 'e2e'
    ? [
        {
          path: '/__gallery',
          lazy: async () => ({ Component: (await import('./pages/Gallery')).Gallery }),
        },
      ]
    : [];

export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <ErrorPage />,
    children: [...galleryRoutes, ...appRoutes],
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}
